import { colorHex, flagUrl, animalUrl, inkColor } from "@/lib/constants";
import { Stamp, Globe, MapPin, BookMarked } from "lucide-react";

const CANON = {
  "United States": "United States of America", USA: "United States of America", US: "United States of America",
};
const canon = (c) => CANON[c] || c;

const BORDER_STYLES = ["dashed", "double", "dotted", "solid"];

// A passport-style stamp: the country's national animal, inked in its flag colour, front and centre.
function CountryStamp({ country, count, regions, idx }) {
  const rot = [-3, 2, -1.5, 3, -2.5, 1.5][idx % 6];
  const borderStyle = BORDER_STYLES[idx % BORDER_STYLES.length];
  const flag = flagUrl(country);
  const animal = animalUrl(country);
  const ink = inkColor(country);
  const label = country === "United States of America" ? "United States" : country;
  return (
    <div
      data-testid={`passport-stamp-${country.replace(/[^a-zA-Z]/g, "-")}`}
      className="relative select-none"
      style={{ transform: `rotate(${rot}deg)` }}
    >
      <div
        className="relative w-44 h-44 rounded-xl flex flex-col items-center justify-between text-center overflow-hidden"
        style={{
          border: `2.5px ${borderStyle} ${ink}`,
          boxShadow: `inset 0 0 0 4px ${ink}1f`,
          background: `radial-gradient(circle at 50% 42%, ${ink}12, ${ink}05 70%)`,
        }}
      >
        <div className="absolute inset-1.5 rounded-lg pointer-events-none" style={{ border: `1px solid ${ink}44` }} />

        {/* top row: brand + flag seal */}
        <div className="relative w-full flex items-center justify-between px-2.5 pt-2">
          <span className="font-tech text-[7.5px] uppercase tracking-[0.18em]" style={{ color: ink }}>Drops of Honey</span>
          <span className="w-6 h-6 rounded-full overflow-hidden grid place-items-center shrink-0" style={{ border: `1.5px solid ${ink}`, boxShadow: `0 0 0 2px ${ink}22` }}>
            {flag ? <img src={flag} alt={`${label} flag`} className="w-full h-full object-cover" loading="lazy" /> : <Stamp className="w-3 h-3" style={{ color: ink }} />}
          </span>
        </div>

        {/* hero: national animal inked in country colour */}
        <div className="relative flex-1 w-full grid place-items-center -my-1">
          {animal ? (
            <div className="relative w-[104px] h-[104px] grid place-items-center">
              <div className="absolute inset-0 rounded-full" style={{ background: ink, opacity: 0.16 }} />
              <img
                src={animal}
                alt={`${label} national animal stamp`}
                data-testid={`passport-animal-${country.replace(/[^a-zA-Z]/g, "-")}`}
                className="relative w-full h-full object-contain"
                style={{ mixBlendMode: "multiply", opacity: 0.95, filter: "contrast(1.35) brightness(1.08)" }}
                loading="lazy"
              />
            </div>
          ) : (
            <Stamp className="w-12 h-12" style={{ color: ink, opacity: 0.5 }} />
          )}
        </div>

        {/* bottom strip: country + counts (legible over art) */}
        <div className="relative w-full px-2 pb-2.5 pt-1" style={{ background: "linear-gradient(to top, rgba(250,246,238,0.94), rgba(250,246,238,0.6) 70%, transparent)" }}>
          <p className="font-display font-bold leading-tight text-[14px]" style={{ color: ink }}>{label}</p>
          <p className="font-tech text-[9.5px] uppercase tracking-widest text-muted-page">
            {count} piece{count > 1 ? "s" : ""}{regions > 0 ? ` · ${regions} region${regions > 1 ? "s" : ""}` : ""}
          </p>
        </div>
      </div>

      {/* postmark count ring */}
      <span
        className="absolute -top-2 -right-2 w-9 h-9 rounded-full grid place-items-center font-tech text-[11px] font-bold rotate-[8deg]"
        style={{ border: `2px solid ${ink}`, color: ink, background: "#fff" }}
      >
        {count}
      </span>
    </div>
  );
}

export function CollectionPassport({ data, ownerName }) {
  const countries = (data?.countries || []).slice().sort((a, b) => b.count - a.count);
  const drilldowns = data?.drilldowns || {};
  const totalRegions = Object.values(drilldowns).reduce((s, rows) => s + rows.length, 0);

  if (!countries.length) {
    return (
      <div className="text-center py-16 surface border rounded-2xl" data-testid="passport-empty">
        <BookMarked className="w-10 h-10 mx-auto text-muted-page mb-3" />
        <p className="font-display text-2xl">Your passport is empty</p>
        <p className="text-muted-page mt-1">Add pieces with a country of origin to earn your first stamp.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="collection-passport">
      <div className="grid grid-cols-3 gap-4">
        <div className="surface border rounded-2xl p-5 text-center" data-testid="passport-stat-countries">
          <div className="flex items-center justify-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><Globe className="w-4 h-4" /> Countries</div>
          <p className="font-display text-3xl font-bold mt-1 brand-text">{countries.length}</p>
        </div>
        <div className="surface border rounded-2xl p-5 text-center" data-testid="passport-stat-regions">
          <div className="flex items-center justify-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><MapPin className="w-4 h-4" /> Regions</div>
          <p className="font-display text-3xl font-bold mt-1">{totalRegions}</p>
        </div>
        <div className="surface border rounded-2xl p-5 text-center" data-testid="passport-stat-pieces">
          <div className="flex items-center justify-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><Stamp className="w-4 h-4" /> Stamped pieces</div>
          <p className="font-display text-3xl font-bold mt-1">{data.total_pieces}</p>
        </div>
      </div>

      {/* Passport book */}
      <div
        className="rounded-3xl p-6 sm:p-8 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #4a3010, #78350f 55%, #5a2c0c)", boxShadow: "0 20px 50px -20px rgba(120,53,15,0.6)" }}
      >
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-amber-200/80">Collector's Passport</p>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-amber-50">{ownerName ? `${ownerName}'s Travels` : "My Travels in Glass"}</h3>
          </div>
          <div className="w-14 h-14 rounded-full grid place-items-center shrink-0" style={{ border: "2px solid rgba(251,191,36,0.5)" }}>
            <BookMarked className="w-6 h-6 text-amber-200" />
          </div>
        </div>
        <div className="rounded-2xl p-5 sm:p-6" style={{ background: "#faf6ee" }}>
          <div className="flex flex-wrap gap-6 justify-center">
            {countries.map((c, i) => {
              const cc = canon(c.country);
              const regions = (drilldowns[cc] || []).length;
              return <CountryStamp key={c.country} country={cc} count={c.count} regions={regions} idx={i} />;
            })}
          </div>
        </div>
        <p className="text-center text-amber-200/70 font-tech text-[11px] uppercase tracking-widest mt-5">
          One stamp earned for every country in your collection
        </p>
      </div>
    </div>
  );
}
